import { useState, type FormEvent } from "react"
import {
  AlertTriangle,
  ArrowRight,
  Tool01,
} from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { toast } from "sonner"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import {
  useOnboarding,
  type CreatedDealership,
} from "@/lib/onboarding/onboarding-context"
import { useDeleteDealership } from "@/lib/api/use-teams"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "./onboarding-shell"
import {
  OnboardingContextPanel,
  DealershipFlowTeaching,
  BodyShopRateTeaching,
  GroupNetworkTeaching,
} from "./context-panel"
import {
  AddRowButton,
  TeamRowCard,
  makeDealershipRow,
  validateRowFields,
  type TeamRow,
} from "./team-row-card"
// Note: `rows` for the dealerships step is lifted into the onboarding
// context so back-nav from step 5 (body shops) doesn't wipe the list of
// dealerships the user already committed.
import { BODYSHOP_PLACEHOLDER_METADATA } from "@/lib/fees/defaults"
import { cx } from "@/lib/utils/cx"

// Step 4 — branches by org type:
//   Dealership      → Connect a body shop (skippable; gate at /estimates/quick
//                     blocks if not connected).
//   Body Shop       → 6-input labor-rate form. Body+paint required. The
//                     dashboard warning + estimate gate read laborRate values
//                     via `feesNotReviewed` (positive rate = reviewed); no
//                     sentinel write. Skippable, but the gate blocks
//                     /estimates/quick until at least one rate is set.
//   DealershipGroup → Add locations + per-location GM email (skippable).

export function ExtraStep() {
  const { selectedType } = useOnboarding()
  if (selectedType === "Dealership") return <ConnectBodyshopExtra />
  if (selectedType === "Bodyshop") return <FeesExtra />
  if (selectedType === "DealershipGroup") return <DealershipsExtra />
  return <FallbackExtra />
}

/* ── Dealership: connect a body shop (REQUIRED) ────────── */

function ConnectBodyshopExtra() {
  const { goBack, finish, createdTeamId } = useOnboarding()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!createdTeamId) return
    const fieldErrors: typeof errors = {}
    if (!name.trim()) fieldErrors.name = "Body shop name is required"
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      fieldErrors.email = "Enter a valid email or leave empty"
    }
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      await teamsApi.connectBodyshop(createdTeamId, {
        bodyshopName: name.trim(),
        generalManagerEmail: email.trim() || undefined,
      })
      toast.success("Body shop connected")
      await finish()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't connect body shop"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <StepLayout
      eyebrow="Step 5 — Setup"
      title="Connect a body shop"
      subtitle="This is who handles the actual repairs once you send an estimate. You can do this later — but the sooner you connect a shop, the sooner estimates can flow."
      hero={
        <OnboardingContextPanel
          teaching={<DealershipFlowTeaching dealershipName="" bodyshopName={name} />}
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <StaggerField index={0}>
          <Field label="Body shop name" error={errors.name} required>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (errors.name) setErrors((er) => ({ ...er, name: undefined }))
              }}
              maxLength={255}
              placeholder="Elite Auto Body"
              className={cx(onboardingInputClass, errors.name && onboardingInputErrorClass)}
              autoFocus
            />
          </Field>
        </StaggerField>

        <StaggerField index={1}>
          <Field
            label="Body shop GM email (optional)"
            error={errors.email}
            hint="If we don't have an account for them yet, we'll email them an invite to set one up."
          >
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (errors.email) setErrors((er) => ({ ...er, email: undefined }))
              }}
              placeholder="manager@shop.com"
              className={cx(onboardingInputClass, errors.email && onboardingInputErrorClass)}
            />
          </Field>
        </StaggerField>

        <StaggerField index={2}>
          <div className="rounded-xl bg-amber-50/60 border border-amber-200/70 px-4 py-3 flex items-start gap-3">
            <Tool01 className="size-4 text-amber-600 mt-0.5 shrink-0" aria-hidden />
            <p className="text-[12.5px] text-amber-900/80 leading-relaxed">
              <span className="font-semibold">You'll need a connected shop before you can send estimates.</span>{" "}
              You can skip this for now and connect one later from{" "}
              <span className="font-semibold">Settings → Body Shops</span>, but you'll
              hit a friendly reminder the first time you try to start an estimate.
            </p>
          </div>
        </StaggerField>

        <StaggerField index={3}>
          <StepActions
            onBack={goBack}
            skip={{
              onSkip: finish,
              disabled: submitting,
              label: "I'll connect a shop later",
            }}
            primary={
              <PrimaryButton loading={submitting} variant="emerald">
                Connect & finish
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}

/* ── Body shop: labor-rate form (REQUIRED-ish, skippable) ── */

const LABOR_FIELD_LABELS: Record<keyof typeof BODYSHOP_PLACEHOLDER_METADATA.laborRate, string> = {
  bodyLabor: "Body labor",
  paintLabor: "Paint labor",
  frameLabor: "Frame labor",
  mechanical: "Mechanical",
  aluminumBody: "Aluminum body",
  aluminumFrame: "Aluminum frame",
}

type LaborField = keyof typeof BODYSHOP_PLACEHOLDER_METADATA.laborRate
type LaborFormState = Record<LaborField, string>

const REQUIRED_LABOR_FIELDS: LaborField[] = ["bodyLabor", "paintLabor"]

const EMPTY_LABOR_FORM: LaborFormState = {
  bodyLabor: "",
  paintLabor: "",
  frameLabor: "",
  mechanical: "",
  aluminumBody: "",
  aluminumFrame: "",
}

function parseRate(v: string): number | null {
  if (!v.trim()) return null
  const n = Number.parseFloat(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

function FeesExtra() {
  const { goBack, finish, createdTeamId } = useOnboarding()
  const [form, setForm] = useState<LaborFormState>(EMPTY_LABOR_FORM)
  const [errors, setErrors] = useState<Partial<Record<LaborField, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [skipping, setSkipping] = useState(false)

  const update = (field: LaborField, value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }))
  }

  const requiredFilled = REQUIRED_LABOR_FIELDS.every((f) => parseRate(form[f]) != null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!createdTeamId) return

    // Validate: required fields must be > 0; optional fields, if present, must
    // also be > 0 (zeroes / negatives are nonsense for labor rates).
    const fieldErrors: Partial<Record<LaborField, string>> = {}
    for (const field of Object.keys(form) as LaborField[]) {
      const raw = form[field].trim()
      if (REQUIRED_LABOR_FIELDS.includes(field) && !raw) {
        fieldErrors[field] = "Required"
        continue
      }
      if (raw && parseRate(raw) == null) {
        fieldErrors[field] = "Enter a rate above $0"
      }
    }
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      return
    }
    setErrors({})

    // Send only the labor-rate fields the user actually filled in. The BE's
    // `applyBodyshopDefaults` hook merges with the seeded placeholders for any
    // omitted fields, so untouched rates stay at placeholder until the user
    // visits Settings → Fees.
    const filled: Partial<Record<LaborField, number>> = {}
    for (const field of Object.keys(form) as LaborField[]) {
      const n = parseRate(form[field])
      if (n != null) filled[field] = n
    }

    setSubmitting(true)
    try {
      // Per-PO decision: no `feesReviewedAt` sentinel. The dashboard warning
      // + estimate gate read laborRate values directly via `feesNotReviewed`
      // (any positive rate counts as reviewed). Saving even one real rate
      // here clears the warning.
      await teamsApi.updateSettings(createdTeamId, {
        metadata: {
          laborRate: filled,
        },
      })
      toast.success("Labor rates saved")
      await finish()
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't save your rates"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSkip = async () => {
    setSkipping(true)
    try {
      await finish()
    } finally {
      setSkipping(false)
    }
  }

  return (
    <StepLayout
      eyebrow="Step 5 — Your shop rates"
      title="Set your labor rates"
      subtitle="These six rates drive how every estimate prices. Set body and paint to start — the others can wait. Placeholder values shown are stand-ins; you should replace them with your real rates."
      hero={
        <OnboardingContextPanel
          teaching={<BodyShopRateTeaching bodyLabor={form.bodyLabor} />}
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <StaggerField index={0}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(Object.keys(LABOR_FIELD_LABELS) as LaborField[]).map((field) => {
              const label = LABOR_FIELD_LABELS[field]
              const placeholder = BODYSHOP_PLACEHOLDER_METADATA.laborRate[field]
              const required = REQUIRED_LABOR_FIELDS.includes(field)
              const error = errors[field]
              return (
                <div key={field} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label htmlFor={`labor-${field}`} className="text-[13px] font-semibold text-zinc-700">
                      {label}
                    </label>
                    <span
                      className={cx(
                        "text-[10.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded",
                        required ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500",
                      )}
                    >
                      {required ? "Required" : "Optional"}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-zinc-400 font-medium">
                      $
                    </span>
                    <input
                      id={`labor-${field}`}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={form[field]}
                      onChange={(e) => update(field, e.target.value)}
                      placeholder={`e.g. ${placeholder}`}
                      className={cx(
                        "w-full h-11 rounded-xl border bg-white pl-7 pr-12 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150 focus:ring-4",
                        error
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500/10"
                          : "border-zinc-200 focus:border-emerald-500 focus:ring-emerald-500/10",
                      )}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-zinc-400 font-medium">
                      / hr
                    </span>
                  </div>
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -3 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-[11.5px] font-medium text-red-600"
                    >
                      {error}
                    </motion.p>
                  )}
                </div>
              )
            })}
          </div>
        </StaggerField>

        <StaggerField index={1}>
          <div className="rounded-xl bg-amber-50/60 border border-amber-200/70 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="size-4 text-amber-600 mt-0.5 shrink-0" aria-hidden />
            <p className="text-[12.5px] text-amber-900/85 leading-relaxed">
              <span className="font-semibold">You'll need real rates before you can run estimates.</span>{" "}
              You can skip this for now and finish in{" "}
              <span className="font-semibold">Settings → Fees</span>, but quick-estimate will
              prompt you until your rates are set.
            </p>
          </div>
        </StaggerField>

        <StaggerField index={2}>
          <StepActions
            onBack={goBack}
            skip={{
              onSkip: handleSkip,
              disabled: submitting || skipping,
              label: "I'll set rates later",
            }}
            primary={
              <PrimaryButton
                loading={submitting}
                disabled={!requiredFilled || skipping}
                variant="emerald"
              >
                Save & finish
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}

/* ── Group: dealerships ────────────────────────────────
 *
 * Step 4 (group). The user adds dealerships one at a time — fill the row,
 * click "Invite GM & add dealership", the row commits (POST + invite) and
 * collapses into a sent confirmation card. Continue is gated on at least
 * one row reaching the sent state.
 *
 * Per row the user MUST provide name + city + state + GM email + GM role.
 * The invite is not optional on a dealership row — these are the people
 * who'll actually run estimates at that location, and the rest of the
 * onboarding flow assumes the GM exists somewhere we can route work to.
 *
 * Each row is its own commit, so a partial set of dealerships is allowed
 * (rows still in draft are simply not created when Continue fires).
 */

function DealershipsExtra() {
  const {
    goBack,
    goNext,
    createdTeamId,
    setCreatedDealerships,
    dealershipRows: rows,
    setDealershipRows: setRows,
  } = useOnboarding()
  // Dealership rows always require the GM invite — the GM is not a
  // separate concept from the dealership, they're the contact for it.
  const inviteMode = "required" as const
  const deleteDealershipMutation = useDeleteDealership(createdTeamId)
  // Track the row we're currently DELETE'ing on the BE so we don't
  // double-fire and so the trash button can disable while in flight.
  const [deletingKey, setDeletingKey] = useState<number | null>(null)

  const sentRows = rows.filter((r) => r.status === "sent")

  const updateRow = (key: number, patch: Partial<TeamRow>) => {
    setRows((rs) =>
      rs.map((r) => (r.key === key ? { ...r, ...patch, errors: undefined } : r)),
    )
  }
  const setRowState = (key: number, patch: Partial<TeamRow>) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }
  const addRow = () => setRows((rs) => [...rs, makeDealershipRow()])

  // Per-row trash. Two paths:
  //   draft  → local-only, but never strand the user with zero rows
  //            (Continue is gated on >=1 sent, but the form still needs
  //            something to render).
  //   sent   → real BE delete. The dealership team and the GM invite were
  //            both created by handleSendRow, so removing the card without
  //            calling DELETE would orphan a team in the parent group and
  //            leave the GM with a live invite to a doomed org.
  const removeRow = async (key: number) => {
    const target = rows.find((r) => r.key === key)
    if (!target) return

    if (target.status === "sent" && target.createdTeamId) {
      if (deletingKey != null) return
      setDeletingKey(key)
      try {
        await deleteDealershipMutation.mutateAsync(target.createdTeamId)
        setRows((rs) => rs.filter((r) => r.key !== key))
        toast.success(`${target.name.trim()} removed`)
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.message : "Couldn't remove this dealership"
        toast.error(msg)
      } finally {
        setDeletingKey(null)
      }
      return
    }

    // Draft row — pure local cleanup. Keep at least one row alive so the
    // form stays interactive after every draft is removed.
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs))
  }

  // Per-row send: validate → addDealership → invite → mark row as sent.
  // Each row owns its own network round-trip so a typo on row 3 never
  // un-creates the dealerships in rows 1 and 2.
  const handleSendRow = async (key: number) => {
    if (!createdTeamId) return
    const current = rows.find((r) => r.key === key)
    if (!current) return
    const { row: validated, ok, willInvite } = validateRowFields(
      current,
      inviteMode,
    )
    setRows((rs) => rs.map((r) => (r.key === key ? validated : r)))
    if (!ok) return

    setRowState(key, { status: "sending" })
    try {
      // Real BE's /teams/{groupId}/group/dealerships creates the dealership team,
      // creates the GM user if they don't exist, links them as owner, and
      // queues the welcome email — all in one shot. So `generalManagerEmail`
      // goes here, NOT via a separate teamsApi.invite() call (that would
      // double-invite). City/state are accepted by the same DTO so the
      // dealership card lands fully populated without a follow-up PATCH.
      const cityTrim = validated.city.trim()
      const stateTrim = validated.state.trim()
      const dg = await teamsApi.addDealership(createdTeamId, {
        dealershipName: validated.name.trim(),
        ...(willInvite
          ? { generalManagerEmail: validated.gmEmail.trim() }
          : {}),
        ...(cityTrim ? { city: cityTrim } : {}),
        ...(stateTrim ? { state: stateTrim } : {}),
      })
      setRowState(key, { status: "sent", createdTeamId: dg.team.id })
      toast.success(
        `${validated.name.trim()} added${willInvite ? " · invite sent" : ""}`,
      )
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't add this dealership"
      toast.error(msg)
      setRowState(key, { status: "draft" })
    }
  }

  const handleContinue = (e: FormEvent) => {
    e.preventDefault()
    if (sentRows.length === 0) return
    const created: CreatedDealership[] = sentRows.map((r) => ({
      teamId: r.createdTeamId!,
      name: r.name.trim(),
      city: r.city.trim(),
      state: r.state.trim(),
    }))
    setCreatedDealerships(created)
    goNext()
  }

  // The "add another" affordance only makes sense when there isn't already
  // a draft row open — otherwise the user is just stacking up empty cards.
  const hasDraftRow = rows.some((r) => r.status === "draft")

  return (
    <StepLayout
      eyebrow="Step 4 — Dealerships"
      title="Add your dealerships"
      subtitle="Each dealership is its own team with its own GM. Fill in the row and click to add — the GM gets an email invite right away. Add one or more."
      hero={
        <OnboardingContextPanel
          teaching={
            <GroupNetworkTeaching
              dealerships={sentRows.map((r) => ({
                name: r.name,
                city: r.city,
                state: r.state,
              }))}
              bodyshops={[]}
              connectionCount={0}
            />
          }
        />
      }
    >
      <form onSubmit={handleContinue} className="space-y-6">
        <StaggerField index={0}>
          <Field
            label="Dealerships"
            hint="Each row commits when you click. Body shops come next."
          >
            <div className="space-y-2.5">
              <AnimatePresence initial={false}>
                {rows.map((r, i) => (
                  <TeamRowCard
                    key={r.key}
                    row={r}
                    index={i}
                    isPrimary={i === 0}
                    canRemove={
                      // Sent rows are always removable (BE delete is wired);
                      // draft rows only when there's another row to fall back
                      // on. Block clicks on the row currently being DELETE'd.
                      deletingKey !== r.key &&
                      (r.status === "sent" || rows.length > 1)
                    }
                    palette="indigo"
                    placeholderName={`Dealership ${i + 1} (e.g. Austin Honda)`}
                    inviteMode={inviteMode}
                    sendLabel="Add dealership & invite GM"
                    onChange={(patch) => updateRow(r.key, patch)}
                    onSend={() => handleSendRow(r.key)}
                    onRemove={() => removeRow(r.key)}
                  />
                ))}
              </AnimatePresence>
              {!hasDraftRow && (
                <AddRowButton onClick={addRow} label="+ Add another dealership" />
              )}
            </div>
          </Field>
        </StaggerField>

        <StaggerField index={1}>
          <StepActions
            onBack={goBack}
            primary={
              <PrimaryButton
                disabled={sentRows.length === 0}
                variant="emerald"
              >
                {sentRows.length === 0
                  ? "Add at least one dealership"
                  : `Continue to body shops${
                      sentRows.length > 1 ? ` (${sentRows.length} added)` : ""
                    }`}
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}

function FallbackExtra() {
  const { goBack, finish } = useOnboarding()
  return (
    <StepLayout
      eyebrow="Almost there"
      title="That's everything we need"
      subtitle="Wrapping up — taking you to your dashboard."
      hero={<OnboardingContextPanel />}
    >
      <StepActions
        onBack={goBack}
        primary={
          <PrimaryButton type="button" onClick={finish} variant="emerald">
            Finish
            <ArrowRight className="size-[18px]" aria-hidden />
          </PrimaryButton>
        }
      />
    </StepLayout>
  )
}
